/**
 * TileGrid - Responsive container for EntityTile components
 *
 * Provides consistent grid/list layouts for tile collections with:
 * - Responsive column configuration
 * - View mode toggle (grid/list) with smooth animations
 * - Empty state integration
 * - Loading skeleton support
 * - Search/filter with styled input
 */

import { ReactNode, useState, CSSProperties } from 'react';
import { Grid, List, Search, X } from 'lucide-react';
import { Skeleton } from './Skeleton';

export type ViewMode = 'grid' | 'list';

export interface TileGridProps<T> {
  // Data
  items: T[];
  renderTile: (item: T, index: number) => ReactNode;
  keyExtractor?: (item: T, index: number) => string;

  // States
  loading?: boolean;
  loadingCount?: number;
  emptyState?: ReactNode;

  // Layout
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  columns?: {
    sm?: number;
    md?: number;
    lg?: number;
  };
  gap?: number;

  // Header
  title?: string;
  headerActions?: ReactNode;
  showViewToggle?: boolean;

  // Search
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // Styling
  style?: CSSProperties;
  className?: string;
}

// Styled view toggle button component
function ViewToggleButton({
  mode,
  currentMode,
  icon: Icon,
  label,
  onClick,
  position,
}: {
  mode: ViewMode;
  currentMode: ViewMode;
  icon: typeof List;
  label: string;
  onClick: () => void;
  position: 'left' | 'right';
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isActive = mode === currentMode;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={label}
      aria-pressed={isActive}
      style={{
        width: 38,
        height: 34,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius:
          position === 'left'
            ? 'var(--radius-md, 8px) 0 0 var(--radius-md, 8px)'
            : '0 var(--radius-md, 8px) var(--radius-md, 8px) 0',
        background: isActive
          ? 'var(--accent, #3b82f6)'
          : isHovered
            ? 'var(--gray-100, #f1f5f9)'
            : 'white',
        color: isActive ? 'white' : 'var(--gray-500, #64748b)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        transform: isHovered && !isActive ? 'scale(1.02)' : 'scale(1)',
      }}
    >
      <Icon size={16} />
    </button>
  );
}

export function TileGrid<T>({
  // Data
  items,
  renderTile,
  keyExtractor = (_, index) => String(index),

  // States
  loading = false,
  loadingCount = 6,
  emptyState,

  // Layout
  viewMode: controlledViewMode,
  onViewModeChange,
  columns = { sm: 1, md: 2, lg: 3 },
  gap = 14,

  // Header
  title,
  headerActions,
  showViewToggle = true,

  // Search
  searchable = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',

  // Styling
  style,
  className,
}: TileGridProps<T>) {
  // Internal view mode state (used if not controlled)
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('list');
  const [searchFocused, setSearchFocused] = useState(false);
  const viewMode = controlledViewMode ?? internalViewMode;

  const handleViewModeChange = (mode: ViewMode) => {
    if (onViewModeChange) {
      onViewModeChange(mode);
    } else {
      setInternalViewMode(mode);
    }
  };

  // Generate responsive grid styles
  const getGridStyles = (): CSSProperties => {
    if (viewMode === 'list') {
      return {
        display: 'flex',
        flexDirection: 'column',
        gap,
      };
    }

    // Grid mode with CSS custom properties for responsive columns
    return {
      display: 'grid',
      gap,
      gridTemplateColumns: `repeat(var(--grid-cols, ${columns.lg || 3}), 1fr)`,
    };
  };

  const hasHeader = title || headerActions || showViewToggle || searchable;

  return (
    <div className={className} style={style}>
      {/* Header section */}
      {hasHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginBottom: '18px',
            flexWrap: 'wrap',
          }}
        >
          {/* Title */}
          {title && (
            <h3
              style={{
                margin: 0,
                fontSize: '17px',
                fontWeight: 600,
                color: 'var(--gray-800, #1e293b)',
                letterSpacing: '-0.01em',
                flex: 1,
              }}
            >
              {title}
            </h3>
          )}

          {/* Search input */}
          {searchable && (
            <div
              style={{
                position: 'relative',
                minWidth: 220,
                flex: title ? undefined : 1,
              }}
            >
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: searchFocused ? 'var(--accent, #3b82f6)' : 'var(--gray-400, #9ca3af)',
                  pointerEvents: 'none',
                  transition: 'color 0.15s ease',
                }}
              />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder={searchPlaceholder}
                style={{
                  width: '100%',
                  padding: '10px 36px 10px 40px',
                  fontSize: '14px',
                  border: searchFocused
                    ? '1px solid var(--accent, #3b82f6)'
                    : '1px solid var(--gray-200, #e5e7eb)',
                  borderRadius: 'var(--radius-lg, 10px)',
                  background: 'white',
                  outline: 'none',
                  boxShadow: searchFocused
                    ? '0 0 0 3px var(--primary-100, #dbeafe)'
                    : '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
              />
              {searchValue && (
                <button
                  onClick={() => onSearchChange?.('')}
                  aria-label="Clear search"
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    background: 'var(--gray-100, #f1f5f9)',
                    borderRadius: '50%',
                    color: 'var(--gray-500, #64748b)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--gray-200, #e5e7eb)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--gray-100, #f1f5f9)';
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          {/* View toggle */}
          {showViewToggle && (
            <div
              style={{
                display: 'flex',
                border: '1px solid var(--gray-200, #e5e7eb)',
                borderRadius: 'var(--radius-md, 8px)',
                overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <ViewToggleButton
                mode="list"
                currentMode={viewMode}
                icon={List}
                label="List view"
                onClick={() => handleViewModeChange('list')}
                position="left"
              />
              <div style={{ width: 1, background: 'var(--gray-200, #e5e7eb)' }} />
              <ViewToggleButton
                mode="grid"
                currentMode={viewMode}
                icon={Grid}
                label="Grid view"
                onClick={() => handleViewModeChange('grid')}
                position="right"
              />
            </div>
          )}

          {/* Custom header actions */}
          {headerActions}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={getGridStyles()}>
          {viewMode === 'list' ? (
            <Skeleton.Card count={loadingCount} />
          ) : (
            Array.from({ length: loadingCount }).map((_, i) => (
              <Skeleton.Card key={i} count={1} />
            ))
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && emptyState}

      {/* Items grid/list */}
      {!loading && items.length > 0 && (
        <>
          {/* Responsive CSS for grid columns */}
          <style>
            {`
              .tile-grid-container {
                --grid-cols: ${columns.lg || 3};
              }
              @media (max-width: 1024px) {
                .tile-grid-container {
                  --grid-cols: ${columns.md || 2};
                }
              }
              @media (max-width: 640px) {
                .tile-grid-container {
                  --grid-cols: ${columns.sm || 1};
                }
              }
            `}
          </style>
          <div className="tile-grid-container" style={getGridStyles()}>
            {items.map((item, index) => (
              <div
                key={keyExtractor(item, index)}
                style={{
                  animation: 'tileGridFadeIn 0.2s ease forwards',
                  animationDelay: `${Math.min(index * 30, 300)}ms`,
                  opacity: 0,
                }}
              >
                {renderTile(item, index)}
              </div>
            ))}
          </div>
          <style>
            {`
              @keyframes tileGridFadeIn {
                from {
                  opacity: 0;
                  transform: translateY(8px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}
          </style>
        </>
      )}
    </div>
  );
}

// Quick access grid configurations
TileGrid.columns = {
  single: { sm: 1, md: 1, lg: 1 },
  double: { sm: 1, md: 2, lg: 2 },
  triple: { sm: 1, md: 2, lg: 3 },
  quad: { sm: 1, md: 2, lg: 4 },
  auto: { sm: 1, md: 2, lg: 3 },
};

export default TileGrid;
